import { useMemo } from 'react'
import { BadgeCheck, Calendar, Gift, Wallet } from 'lucide-react'
import type { User } from '../../lib/types'

type DiscountBreakdown = {
  ctv?: number | string | null
  rank?: number | string | null
  final_user?: number | string | null
  applied_source?: string | null
}

type BenefitMetadata = {
  original_price?: number | string | null
  quantity_in_order?: number | string | null
  discount_source?: string | null
  discount_breakdown?: DiscountBreakdown | null
  commission_amount?: number | string | null
}

export type CtvBenefitTransaction = {
  id: string
  created_at: string
  amount: number
  status: string
  discount_source?: string | null
  commission_amount?: number | null
  product_name?: string | null
  variant_name?: string | null
  metadata?: BenefitMetadata
}

interface CtvBenefitsHistoryProps {
  user: User
  transactions: CtvBenefitTransaction[]
  loading?: boolean
}

const CTV_DISCOUNT_PERCENT = 5

const toNumber = (value: unknown) => {
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

const formatMoney = (value: number) => `${Math.max(0, Math.round(value)).toLocaleString('vi-VN')}đ`

const getStatusBadge = (status: string) => {
  const key = (status || '').toLowerCase()
  const statusMap: Record<string, { text: string; cls: string }> = {
    pending: { text: 'Chờ xử lý', cls: 'bg-amber-100 text-amber-800' },
    processing: { text: 'Đang xử lý', cls: 'bg-blue-100 text-blue-800' },
    completed: { text: 'Thành công', cls: 'bg-green-100 text-green-800' },
    refunded: { text: 'Đã hoàn tiền', cls: 'bg-rose-100 text-rose-800' },
    failed: { text: 'Thất bại', cls: 'bg-red-100 text-red-800' },
  }
  const state = statusMap[key] || statusMap.pending
  return <span className={`px-2 py-1 text-xs font-medium rounded-full ${state.cls}`}>{state.text}</span>
}

export default function CtvBenefitsHistory({ user, transactions, loading = false }: CtvBenefitsHistoryProps) {
  const normalized = useMemo(() => {
    return transactions.map(tx => {
      const breakdown = tx.metadata?.discount_breakdown
      const rankPercent = Math.max(0, toNumber(breakdown?.rank))
      const ctvPercent = Math.max(0, toNumber(breakdown?.ctv))
      const finalUserPercent = Math.max(0, toNumber(breakdown?.final_user))

      const source = String(
        tx.discount_source
        || tx.metadata?.discount_source
        || breakdown?.applied_source
        || ''
      ).toUpperCase()

      const quantity = Math.max(1, Math.trunc(toNumber(tx.metadata?.quantity_in_order) || 1))
      const originalUnitPrice = Math.max(0, toNumber(tx.metadata?.original_price))
      const originalTotal = originalUnitPrice * quantity
      const paidAmount = Math.abs(toNumber(tx.amount))
      const ctvAppliedPercent = source === 'USER'
        ? Math.max(0, Math.min(ctvPercent, finalUserPercent - rankPercent))
        : 0
      const ctvSavedAmount = Math.max(0, Math.round(originalTotal * ctvAppliedPercent / 100))
      const commissionAmount = Math.max(0, toNumber(tx.commission_amount ?? tx.metadata?.commission_amount))

      return {
        tx,
        source,
        ctvPercent,
        ctvAppliedPercent,
        originalTotal,
        paidAmount,
        ctvSavedAmount,
        commissionAmount,
      }
    })
  }, [transactions])

  const totalSaved = useMemo(
    () => normalized.reduce((sum, item) => sum + item.ctvSavedAmount, 0),
    [normalized]
  )

  const commissionBalance = Math.max(0, Number(user.commission_balance || 0))

  if (!user.is_ctv) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
        <Gift className="h-10 w-10 text-gray-400 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Tab ưu đãi CTV</h3>
        <p className="text-sm text-gray-600">Tài khoản của bạn chưa bật cộng tác viên.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3">
          <div className="text-xs text-gray-600 mb-1">Trạng thái CTV</div>
          <div className="text-sm font-semibold text-emerald-700 inline-flex items-center gap-1.5">
            <BadgeCheck className="h-4 w-4" />
            Đang bật (+{CTV_DISCOUNT_PERCENT}%)
          </div>
        </div>
        <div className="rounded-lg border border-amber-100 bg-amber-50 p-3">
          <div className="text-xs text-gray-600 mb-1">Tiết kiệm CTV đã áp dụng</div>
          <div className="text-base font-bold text-amber-700">{formatMoney(totalSaved)}</div>
        </div>
        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
          <div className="text-xs text-gray-600 mb-1">Hoa hồng CTV tích lũy</div>
          <div className="text-base font-bold text-blue-700 inline-flex items-center gap-1.5">
            <Wallet className="h-4 w-4" />
            {formatMoney(commissionBalance)}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-gray-500">Đang cập nhật dữ liệu ưu đãi...</div>
      ) : null}

      {normalized.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
          <p className="text-sm text-gray-600">Chưa có giao dịch ưu đãi CTV nào.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {normalized.map(item => (
            <div key={item.tx.id} className="bg-white rounded-lg border p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                <div>
                  <h4 className="text-sm sm:text-base font-semibold text-gray-900">
                    {item.tx.product_name || 'Sản phẩm'}{item.tx.variant_name ? ` - ${item.tx.variant_name}` : ''}
                  </h4>
                  <div className="text-xs text-gray-500 inline-flex items-center gap-1 mt-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {new Date(item.tx.created_at).toLocaleString('vi-VN')}
                  </div>
                </div>
                {getStatusBadge(item.tx.status)}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-xs text-gray-500">Giá gốc đơn</div>
                  <div className="font-medium text-gray-900">{formatMoney(item.originalTotal)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Đã thanh toán</div>
                  <div className="font-medium text-gray-900">{formatMoney(item.paidAmount)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Nguồn giảm giá</div>
                  <div className="font-medium text-gray-900">{item.source || 'NONE'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Ưu đãi CTV khả dụng</div>
                  <div className="font-medium text-gray-900">{item.ctvPercent}%</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Ưu đãi CTV đã áp dụng</div>
                  <div className="font-medium text-amber-700">{item.ctvAppliedPercent}%</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Tiết kiệm từ CTV</div>
                  <div className="font-medium text-emerald-700">{formatMoney(item.ctvSavedAmount)}</div>
                </div>
              </div>

              {item.commissionAmount > 0 ? (
                <div className="mt-3 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded px-3 py-2">
                  Hoa hồng phát sinh từ đơn này: {formatMoney(item.commissionAmount)}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
